import { Aircraft, CalibrationPoint } from "../../../../../types";
import { formatNumber } from "../../../../../utils";
import {
  feetToMeters,
  squareFeetToSquareMeters,
} from "../../../../../utils/math";
import { OrientationType } from "../../../../../utils/spots";
import { formatSizeCategories } from "../../../../Ramp/TemporalTest/utils/formatSizeCategory";
import {
  getAircraftBounds,
  getCanonicalAircraftForSpot,
} from "../../RampEditorV2/RampCanvasV2/canonicalAircraft";
import { calculateArea, getCenter, getSnapPoint } from "./math";
import {
  LineMetadata,
  ReferencePoint,
  Shape,
  SIZE_CATEGORY_OPTIONS,
  SizeCategory,
  SpotMetadata,
} from "./RampCanvas";

interface CanvasState {
  pixelsToFeet: (pixels: number) => number;
  showMeasurements: boolean;
  showGuideLines: boolean;
  pixelsToFeetRatio: number;
  activeShapes: string[];
  patterns: Record<string, CanvasPattern>;
  zoomLevel: number;
  isMetric: boolean;
  rampDepth?: number;
}

interface ReferenceAircraftData {
  aircraft: Aircraft | null;
  position: { x: number; y: number; angle: number }; // in feet
  loadedImage: HTMLImageElement | null;
  isSelected: boolean;
}

interface DrawingState {
  shapes: Shape[];
  currentShape?: Shape;
  highlightedShapes: string[];
  mousePosition: { x: number; y: number } | null;
  calibrationPoints?: CalibrationPoint[];
  showCalibrationPoints?: boolean;
  highlightedCalibrationPoint?: string | null;
  previewSpots?: Shape[];
  referenceAircraft?: ReferenceAircraftData;
  //
  canvasRef: React.RefObject<HTMLCanvasElement>;
  pixelsToFeet: (pixels: number) => number;
  showMeasurements: boolean;
  showGuideLines: boolean;
  pixelsToFeetRatio: number;
  activeShapes: string[];
  patterns: Record<string, CanvasPattern>;
  isShiftPressed: boolean;
  zoomLevel: number;
  isMetric: boolean;
  rampDepth?: number;
}

export const drawMain = (
  context: CanvasRenderingContext2D,
  drawingState: DrawingState
) => {
  const {
    shapes,
    currentShape,
    highlightedShapes,
    mousePosition,
    calibrationPoints,
    showCalibrationPoints,
    highlightedCalibrationPoint,
    previewSpots,
    referenceAircraft,
    //
    canvasRef,
    pixelsToFeet,
    showMeasurements,
    showGuideLines,
    pixelsToFeetRatio,
    activeShapes,
    patterns,
    isShiftPressed,
    zoomLevel,
    isMetric,
    rampDepth,
  } = drawingState;
  context.clearRect(0, 0, context.canvas.width, context.canvas.height);

  // Filter out invisible shapes (V2 layer panel feature)
  const visibleShapes = shapes.filter((shape) => shape.visible !== false);

  const orderedShapes = [
    ...visibleShapes.filter((shape) => !activeShapes.includes(shape.id)),
    ...visibleShapes.filter((shape) => activeShapes.includes(shape.id)),
  ];

  orderedShapes.forEach((shape, index) => {
    const isHighlighted = highlightedShapes.includes(shape.id);
    drawShape(
      context,
      shape,
      index,
      {
        pixelsToFeet,
        showMeasurements,
        showGuideLines,
        pixelsToFeetRatio,
        activeShapes,
        patterns,
        zoomLevel,
        isMetric,
        rampDepth,
      },
      isHighlighted
    );
  });

  // Draw preview spots with semi-transparent appearance
  if (previewSpots && previewSpots.length > 0) {
    context.save();
    context.globalAlpha = 0.3;
    previewSpots.forEach((previewSpot, index) => {
      drawShape(
        context,
        previewSpot,
        index,
        {
          pixelsToFeet,
          showMeasurements,
          showGuideLines,
          pixelsToFeetRatio,
          activeShapes,
          patterns,
          zoomLevel,
          isMetric,
          rampDepth,
        },
        false
      );
    });
    context.restore();
  }

  // Draw reference aircraft if present
  if (referenceAircraft?.aircraft && referenceAircraft.loadedImage) {
    // Convert position from feet to pixels
    const positionInPixels = {
      x: referenceAircraft.position.x * pixelsToFeetRatio,
      y: referenceAircraft.position.y * pixelsToFeetRatio,
      angle: referenceAircraft.position.angle,
    };
    drawReferenceAircraft(
      context,
      referenceAircraft.aircraft,
      positionInPixels,
      referenceAircraft.loadedImage,
      referenceAircraft.isSelected,
      pixelsToFeetRatio,
      showMeasurements,
      isMetric
    );
  }

  // ok we're done drawing all the shapes. now we need to draw the current shape
  if (!currentShape) {
    return;
  }
  context.beginPath();
  context.lineWidth = 3;
  context.strokeStyle = "blue";
  context.setLineDash(lineStyleToDash(currentShape.line_style));
  context.lineWidth = currentShape.line_width;
  currentShape.points.forEach((point, pointIndex) => {
    if (pointIndex === 0) {
      context.moveTo(point.x, point.y);
    } else {
      context.lineTo(point.x, point.y);
    }
  });
  context.stroke();

  currentShape.points.forEach((point, index) => {
    if (index === 0) {
      context.moveTo(point.x, point.y);
    } else {
      context.lineTo(point.x, point.y);
    }

    // Draw points with red fill and white outline
    drawPoint(context, point.x, point.y);
  });

  // Draw the "live" line to the current mouse position if in progress
  if (mousePosition && currentShape.points.length > 0) {
    const lastPoint = currentShape.points[currentShape.points.length - 1];
    context.beginPath();
    // if Shift is pressed, snap the line to the nearest 45 degree angle
    if (isShiftPressed) {
      const snapPoint = getSnapPoint(mousePosition, lastPoint);
      context.moveTo(lastPoint.x, lastPoint.y);
      context.lineTo(snapPoint.x, snapPoint.y);
    } else {
      context.moveTo(lastPoint.x, lastPoint.y);
      context.lineTo(mousePosition.x, mousePosition.y);
    }

    context.strokeStyle = "blue";
    context.setLineDash(lineStyleToDash(currentShape.line_style));
    context.lineWidth = currentShape.line_width;
    context.lineWidth = 2;
    context.stroke();

    // Calculate the length of the line segment
    const length = pixelsToFeet(
      Math.hypot(mousePosition.x - lastPoint.x, mousePosition.y - lastPoint.y)
    );

    if (showMeasurements) {
      // Draw the length of the line segment
      const lengthFormatted = isMetric
        ? `${formatNumber(feetToMeters(length), 1)} m`
        : `${formatNumber(length, 1)} ft`;
      drawTextPill(
        context,
        (lastPoint.x + mousePosition.x) / 2,
        (lastPoint.y + mousePosition.y) / 2,
        lengthFormatted,
        12,
        rampDepth,
        zoomLevel
      );
    }
  }

  // check if mouse position is close to a point
  if (mousePosition) {
    // convert mousePosition to canvas coordinates
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) {
      return;
    }
    currentShape.points.forEach((point) => {
      if (
        Math.hypot(point.x - mousePosition.x, point.y - mousePosition.y) < 10
      ) {
        drawPoint(context, point.x, point.y, null, null, "limegreen", "white");
      }
    });
  }

  // Draw guide lines if enabled
  if (showGuideLines) {
    drawGuideLines(context, context.canvas.width, context.canvas.height);
  }

  // Draw calibration points if enabled
  if (showCalibrationPoints && calibrationPoints) {
    calibrationPoints.forEach((point) => {
      drawCalibrationPoint(
        context,
        point,
        pixelsToFeetRatio,
        highlightedCalibrationPoint === point.id
      );
    });
  }
};

export const drawShape = (
  context: CanvasRenderingContext2D,
  shape: Shape,
  index: number,
  state: CanvasState,
  isHighlighted: boolean
) => {
  const hoveredShapeIndex = null;
  const highlightedColor = `#32cd3270`;
  const strokeStyle = isHighlighted ? highlightedColor : shape.stroke_style;
  const fillStyle = isHighlighted ? highlightedColor : shape.fill_style;
  const {
    pixelsToFeet,
    showMeasurements,
    pixelsToFeetRatio,
    activeShapes,
    patterns,
    isMetric,
  } = state;
  if (shape.type === "Text") {
    const fontSize = Math.min(128, Math.max(12, shape.font_size));

    drawText(
      context,
      shape.points[0].x,
      shape.points[0].y,
      shape.text,
      strokeStyle,
      fontSize,
      hoveredShapeIndex === index || activeShapes.includes(shape.id),
      state.rampDepth
    );
    return;
  }

  if (shape.type === "Tie Down T" || shape.type === "Double T") {
    // Draw the shape
    context.beginPath();
    context.lineWidth = 3;
    context.strokeStyle = strokeStyle;
    context.fillStyle = fillStyle;
    context.setLineDash(lineStyleToDash(shape.line_style));
    context.lineWidth = shape.line_width;
    const center = getCenter(shape.points);
    // horizontal line
    context.beginPath();
    context.moveTo(shape.points[0].x, shape.points[0].y);
    context.lineTo(shape.points[1].x, shape.points[1].y);
    context.stroke();
    // Vertical line
    context.beginPath();

    context.stroke();
    const angle = Math.atan2(
      shape.points[1].y - shape.points[0].y,
      shape.points[1].x - shape.points[0].x
    );
    const length = Math.hypot(
      shape.points[1].x - shape.points[0].x,
      shape.points[1].y - shape.points[0].y
    );
    const verticalLength = length / 2; // Adjust this value as needed
    const verticalPoint1 = {
      x: center.x - verticalLength * Math.sin(angle),
      y: center.y + verticalLength * Math.cos(angle),
    };
    const verticalPoint2 = {
      x: center.x + verticalLength * Math.sin(angle),
      y: center.y - verticalLength * Math.cos(angle),
    };
    context.moveTo(verticalPoint1.x, verticalPoint1.y);
    context.lineTo(verticalPoint2.x, verticalPoint2.y);
    context.stroke();

    // add the second line for the Double T
    if (shape.type === "Double T") {
      context.moveTo(shape.points[2].x, shape.points[2].y);
      context.lineTo(shape.points[3].x, shape.points[3].y);
      context.stroke();
    }
  }

  // draw the compass rose
  if (shape.type === "Compass Rose") {
    const center = getCenter(shape.points);
    // Calculate angle based on the first two points directly
    const angle = Math.atan2(
      shape.points[1].y - shape.points[0].y,
      shape.points[1].x - shape.points[0].x
    );
    const length = Math.hypot(
      shape.points[1].x - shape.points[0].x,
      shape.points[1].y - shape.points[0].y
    );
    const radius = length / 2;

    // Save context state
    context.save();
    context.translate(center.x, center.y);
    context.rotate(angle);

    // Draw the outer circle
    context.beginPath();
    context.arc(0, 0, radius, 0, 2 * Math.PI);
    context.strokeStyle = strokeStyle;
    context.lineWidth = shape.line_width || 8;
    context.fillStyle = "white";
    context.stroke();
    context.fill();

    // Draw direction lines
    const lineCount = 8;
    context.beginPath();
    for (let i = 0; i < lineCount; i++) {
      const lineAngle = (Math.PI * 2 * i) / lineCount;
      const innerRadius = radius * 0.2;
      const outerRadius = radius * 0.9;

      context.moveTo(
        innerRadius * Math.sin(lineAngle),
        innerRadius * Math.cos(lineAngle)
      );
      context.lineTo(
        outerRadius * Math.sin(lineAngle),
        outerRadius * Math.cos(lineAngle)
      );
    }
    context.lineWidth = 1.25;
    context.strokeStyle = strokeStyle;
    context.stroke();

    // Draw the N label at the top
    context.fillStyle = strokeStyle;
    context.font = `bold ${radius * 0.65}px Lexend Deca`;
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText("N", 0, -radius * 1.35);

    // Draw the diamond/arrow pointing north and south
    // North-pointing triangle (darker)
    context.beginPath();
    context.moveTo(0, -radius * 0.7); // Top point
    context.lineTo(radius * 0.3, 0); // Right point
    context.lineTo(-radius * 0.3, 0); // Left point
    context.closePath();
    context.fillStyle = strokeStyle;
    context.fill();

    // South-pointing triangle (lighter)
    context.beginPath();
    context.moveTo(0, radius * 0.7); // Bottom point
    context.lineTo(radius * 0.3, 0); // Right point
    context.lineTo(-radius * 0.3, 0); // Left point
    context.closePath();
    context.fillStyle = "white"; // Reset fill style to transparent
    context.strokeStyle = strokeStyle; // Use the same stroke style
    context.fill();
    context.stroke();

    // Draw the inner white circle
    context.beginPath();
    context.arc(0, 0, radius * 0.1, 0, 2 * Math.PI);
    context.fillStyle = "white";
    context.fill();
    context.strokeStyle = strokeStyle;
    context.lineWidth = 1;
    context.stroke();

    // Restore context state
    context.restore();
  }

  if (shape.type === "Circle" || shape.type === "Helipad") {
    const center = getCenter(shape.points);
    const radius =
      Math.hypot(
        shape.points[1].x - shape.points[0].x,
        shape.points[1].y - shape.points[0].y
      ) / 2;
    context.setLineDash(lineStyleToDash(shape.line_style));
    context.lineWidth = shape.line_width;
    context.strokeStyle = strokeStyle;
    context.beginPath();
    context.arc(center.x, center.y, radius, 0, 2 * Math.PI);
    context.stroke();
    if (fillStyle) {
      context.fillStyle = fillStyle;
      context.fill();
    }
  }

  if (shape.type === "Spot") {
    context.setLineDash(lineStyleToDash(shape.line_style));
    context.lineWidth = shape.line_width;
    context.strokeStyle = strokeStyle;
    context.beginPath();
    context.moveTo(shape.points[0].x, shape.points[0].y);
    context.lineTo(shape.points[1].x, shape.points[1].y);
    context.lineTo(shape.points[2].x, shape.points[2].y);
    context.lineTo(shape.points[3].x, shape.points[3].y);
    context.closePath();
    context.stroke();

    // Draw property badges
    drawSpotPropertyBadges(context, shape, state);
  }

  // Draw the shape
  context.beginPath();
  context.lineWidth = 3;
  context.setLineDash(lineStyleToDash(shape.line_style));
  context.lineWidth = shape.line_width;
  context.strokeStyle = strokeStyle;
  context.fillStyle = fillStyle;
  shape.points.forEach((point, pointIndex) => {
    if (pointIndex === 0) {
      context.moveTo(point.x, point.y);
    } else {
      context.lineTo(point.x, point.y);
    }
  });

  // Areas should be a closed shape. And only closed shapes need a fill()
  // for Areas, we'll show the sqft of the area
  if (shape.type === "Area") {
    context.closePath();
    if (fillStyle || shape.fill_pattern) {
      if (fillStyle) {
        context.fillStyle = fillStyle;
      } else {
        context.fillStyle = patterns[shape.fill_pattern];
      }
      context.fill();
    }
    context.stroke();
    if (
      !shape.locked &&
      (hoveredShapeIndex === index || activeShapes.includes(shape.id))
    ) {
      const area = calculateArea(shape.points, 1 / pixelsToFeetRatio);
      if (showMeasurements) {
        const center = getCenter(shape.points);
        const areaFormatted = isMetric
          ? `${formatNumber(squareFeetToSquareMeters(area), 1)} m²`
          : `${formatNumber(area, 1)} ft²`;
        drawTextPill(context, center.x, center.y, areaFormatted, 12, state.rampDepth, state.zoomLevel);
      }
    }
  }

  // Zones should be closed shapes with solid white outline and zone name label
  if (shape.type === "Zone") {
    context.closePath();
    // Fill zone if fillStyle is defined (typically zones have no fill)
    if (fillStyle) {
      context.fillStyle = fillStyle;
      context.fill();
    }
    context.stroke();
    // Display zone name at center
    const center = getCenter(shape.points);
    const zoneName = shape.text || "Zone";
    const label = zoneName;

    // Draw zone label with database font_size
    const fontSize = Math.min(128, Math.max(12, shape.font_size || 14));
    drawText(
      context,
      center.x,
      center.y,
      label,
      "#FFFFFF", // White text to match white outline
      fontSize,
      false,
      state.rampDepth
    );
  }

  if (
    shape.type === "Tie Down T" ||
    shape.type === "Double T" ||
    shape.type === "Circle" ||
    shape.type === "Helipad" ||
    shape.type === "Compass Rose" ||
    shape.type === "Spot" ||
    shape.type === "Zone"
  ) {
    context.closePath();
  }
  if (
    shape.type !== "Tie Down T" &&
    shape.type !== "Double T" &&
    shape.type !== "Circle" &&
    shape.type !== "Helipad" &&
    shape.type !== "Compass Rose" &&
    shape.type !== "Spot" &&
    shape.type !== "Zone"
  ) {
    context.stroke();
  }

  // Draw the text if the shape has any (exclude Zones as they have their own specialized rendering)
  if (shape.text && shape.type !== "Zone") {
    // For Line shapes, check metadata for visibility/position
    const lineMetadata =
      shape.type === "Line" ? (shape.metadata as LineMetadata) : null;
    const showText = lineMetadata?.show_text !== false; // Default: true

    if (!showText) {
      // Skip - label explicitly hidden
    } else if (shape.type === "Helipad") {
      // Helipad: "H" takes up 80% of the width/height of the shape
      const center = getCenter(shape.points);
      const segmentSize = Math.hypot(
        shape.points[1].x - shape.points[0].x,
        shape.points[1].y - shape.points[0].y
      );
      const fontSize = 0.8 * segmentSize;
      drawText(
        context,
        center.x,
        center.y,
        shape.text,
        strokeStyle,
        fontSize,
        null,
        state.rampDepth
      );
    } else if (shape.type === "Line" && shape.points.length >= 2) {
      // Line-specific positioning (start/middle/end)
      const position = lineMetadata?.text_position || "middle";
      let labelX: number, labelY: number;

      if (position === "start") {
        // Near first point - 10% offset toward second point to avoid vertex overlap
        const p0 = shape.points[0];
        const p1 = shape.points[1];
        labelX = p0.x + (p1.x - p0.x) * 0.1;
        labelY = p0.y + (p1.y - p0.y) * 0.1;
      } else if (position === "end") {
        // Near last point - 10% offset toward second-to-last point
        const pLast = shape.points[shape.points.length - 1];
        const pPrev = shape.points[shape.points.length - 2];
        labelX = pLast.x + (pPrev.x - pLast.x) * 0.1;
        labelY = pLast.y + (pPrev.y - pLast.y) * 0.1;
      } else {
        // 'middle' - average of all points (getCenter uses polygon formula which doesn't work for lines)
        labelX =
          shape.points.reduce((sum, p) => sum + p.x, 0) / shape.points.length;
        labelY =
          shape.points.reduce((sum, p) => sum + p.y, 0) / shape.points.length;
      }

      // Add perpendicular offset so text appears beside the line, not on it
      const dx = shape.points[1].x - shape.points[0].x;
      const dy = shape.points[1].y - shape.points[0].y;
      const lineLength = Math.hypot(dx, dy);
      if (lineLength > 0) {
        // Perpendicular unit vector (rotated 90 degrees)
        const perpX = -dy / lineLength;
        const perpY = dx / lineLength;
        // Offset text perpendicular to line
        const offset = 15;
        labelX += perpX * offset;
        labelY += perpY * offset;
      }

      const fontSize = Math.min(128, Math.max(12, shape.font_size));
      drawText(
        context,
        labelX,
        labelY,
        shape.text,
        strokeStyle,
        fontSize,
        false,
        state.rampDepth
      );
    } else {
      // Other shapes - existing center logic
      const center = getCenter(shape.points);
      const fontSize = Math.min(128, Math.max(12, shape.font_size));
      drawText(
        context,
        center.x,
        center.y,
        shape.text,
        strokeStyle,
        fontSize,
        false,
        state.rampDepth
      );
    }
  }

  // Annotate the line segments with their lengths
  if (hoveredShapeIndex === index || activeShapes.includes(shape.id)) {
    shape.points.forEach((point, pointIndex) => {
      // this is the "closing" segment of a shape. we don't need to draw the length
      // for tha tone since the Line is not a closed form.
      if (
        (shape.type === "Line" ||
          shape.type === "Tie Down T" ||
          shape.type === "Double T" ||
          shape.type === "Circle" ||
          shape.type === "Helipad" ||
          shape.type === "Compass Rose" ||
          shape.type === "Spot" ||
          shape.type === "Zone") &&
        pointIndex === shape.points.length - 1
      ) {
        return;
      }

      const nextPoint = shape.points[(pointIndex + 1) % shape.points.length];
      const length = pixelsToFeet(
        Math.hypot(nextPoint.x - point.x, nextPoint.y - point.y)
      );
      if (showMeasurements) {
        if (shape.type === "Circle" && pointIndex !== 1) {
          return;
        }
        if (shape.type === "Helipad" && pointIndex !== 1) {
          return;
        }
        if (shape.type === "Tie Down T" && pointIndex !== 2) {
          return;
        }
        if (shape.type === "Double T" && pointIndex !== 2) {
          return;
        }
        if (shape.type === "Compass Rose" && pointIndex !== 2) {
          return;
        }
        if (shape.type === "Spot" && pointIndex !== 0) {
          return;
        }
        if (!shape.locked) {
          const lengthFormatted = isMetric
            ? `${formatNumber(feetToMeters(length), 1)} m`
            : `${formatNumber(length, 1)} ft`;
          drawTextPill(
            context,
            (point.x + nextPoint.x) / 2,
            (point.y + nextPoint.y) / 2,
            lengthFormatted,
            12,
            state.rampDepth,
            state.zoomLevel
          );
        }
      }
    });
  }

  // Draw points with red fill and white outline
  if (
    !shape.locked &&
    (hoveredShapeIndex === index || activeShapes.includes(shape.id))
  ) {
    shape.points.forEach((point) => drawPoint(context, point.x, point.y));
  }
};

export const drawPoint = (
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number = 4,
  lineWidth: number = 2,
  fillStyle: string = "red",
  strokeStyle: string = "white"
) => {
  context.fillStyle = fillStyle;
  context.strokeStyle = strokeStyle;
  context.lineWidth = lineWidth;
  context.setLineDash([]);
  context.beginPath();
  context.arc(x, y, radius, 0, Math.PI * 2); // 6px radius for the point
  context.fill();
  context.stroke();
};

export const measureText = (
  context: CanvasRenderingContext2D,
  shape: Shape
) => {
  const text = shape.text;
  const fontSize = shape.font_size;
  context.font = `${fontSize}px Lexend Deca`;
  const metrics = context.measureText(text);
  return metrics;
};

export const drawText = (
  context: CanvasRenderingContext2D,
  midX: number,
  midY: number,
  text: string,
  color: string = "black",
  fontSize: number = 12,
  border: boolean = false,
  rampDepth?: number
) => {
  // Scale font based on canvas height relative to ramp depth
  // This provides consistent text sizing across Settings and Main App
  // Canvas height already includes zoom, so no separate zoom scaling needed
  if (rampDepth && rampDepth > 0) {
    fontSize = fontSize * (context.canvas.height / rampDepth);
  }

  const padding = 10 * (context.canvas.height / (rampDepth || context.canvas.height));
  context.font = `${fontSize}px Lexend Deca`;
  context.fillStyle = color;

  // Measure text width
  const textMetrics = context.measureText(text);
  const textWidth = textMetrics.width;

  // Calculate text height (approximation using font size)
  const textHeight = fontSize;

  // Adjust position to center text
  const x = midX - textWidth / 2;
  const y = midY + textHeight / 3; // Adjust to align the vertical center

  // Calculate box dimensions with padding
  const boxX = x - padding;
  const boxY = y - textHeight - padding;
  const boxWidth = textWidth + padding * 2;
  const boxHeight = textHeight + padding * 2;

  if (border) {
    // Draw box border
    context.strokeStyle = "blue";
    context.strokeRect(boxX, boxY, boxWidth, boxHeight);
  }

  // Draw the text
  context.fillStyle = color;
  context.fillText(text, x, y);
};

export const drawTextPill = (
  context: CanvasRenderingContext2D,
  midX: number,
  midY: number,
  text: string,
  fontSize: number = 12,
  rampDepth?: number,
  zoomLevel: number = 1
) => {
  // Apply zoom clamping like MeasuringTool (max at 1, min at 0.5)
  const clampedZoom = Math.max(0.5, Math.min(1, zoomLevel));
  // Scale based on canvas height relative to ramp depth, then apply zoom
  const scaleFactor = rampDepth && rampDepth > 0
    ? (context.canvas.height / rampDepth) * clampedZoom
    : clampedZoom;

  // Apply scaling then clamp to min/max bounds (in pixels)
  const MIN_FONT_SIZE = 10;
  const MAX_FONT_SIZE = 20;
  fontSize = Math.max(MIN_FONT_SIZE, Math.min(MAX_FONT_SIZE, fontSize * scaleFactor));

  // Scale padding/radius proportionally to font size (keeps pill proportional to text)
  const paddingRatio = fontSize / 12; // 12 is the base font size
  const padding = 4 * paddingRatio;
  const radius = 5 * paddingRatio;

  // Draw a rounded rectangle for the distance box
  context.font = `${fontSize}px Lexend Deca`;
  const textWidth = context.measureText(text).width;
  const boxWidth = textWidth + padding * 2;
  const boxHeight = fontSize + padding * 2;

  context.fillStyle = "white";
  context.strokeStyle = "black";
  context.lineWidth = Math.max(1, paddingRatio);

  context.beginPath();
  context.moveTo(midX - boxWidth / 2 + radius, midY - boxHeight / 2);
  context.lineTo(midX + boxWidth / 2 - radius, midY - boxHeight / 2);
  context.quadraticCurveTo(
    midX + boxWidth / 2,
    midY - boxHeight / 2,
    midX + boxWidth / 2,
    midY - boxHeight / 2 + radius
  );
  context.lineTo(midX + boxWidth / 2, midY + boxHeight / 2 - radius);
  context.quadraticCurveTo(
    midX + boxWidth / 2,
    midY + boxHeight / 2,
    midX + boxWidth / 2 - radius,
    midY + boxHeight / 2
  );
  context.lineTo(midX - boxWidth / 2 + radius, midY + boxHeight / 2);
  context.quadraticCurveTo(
    midX - boxWidth / 2,
    midY + boxHeight / 2,
    midX - boxWidth / 2,
    midY + boxHeight / 2 - radius
  );
  context.lineTo(midX - boxWidth / 2, midY - boxHeight / 2 + radius);
  context.quadraticCurveTo(
    midX - boxWidth / 2,
    midY - boxHeight / 2,
    midX - boxWidth / 2 + radius,
    midY - boxHeight / 2
  );
  context.closePath();
  context.fill();
  context.stroke();

  context.fillStyle = "black";
  context.fillText(text, midX - textWidth / 2, midY + fontSize / 2 - padding);
};

// Helper function to calculate spot bounding box from points
const getSpotBounds = (
  points: { x: number; y: number }[]
): {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  width: number;
  height: number;
} => {
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  return {
    minX: Math.min(...xs),
    maxX: Math.max(...xs),
    minY: Math.min(...ys),
    maxY: Math.max(...ys),
    width: Math.max(...xs) - Math.min(...xs),
    height: Math.max(...ys) - Math.min(...ys),
  };
};

// Helper to check if two arrays are equal (for size comparison)
const arraysEqual = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

// Check if allowedSizes is non-default for spot's size
const isNonDefaultSizes = (
  spotSize: number,
  allowedSizes?: SizeCategory[]
): boolean => {
  if (!allowedSizes || allowedSizes.length === 0) return false;

  // Determine spot's default category based on size
  let defaultCategory: SizeCategory = "large"; // fallback
  for (const option of SIZE_CATEGORY_OPTIONS) {
    if (spotSize <= option.maxFeet) {
      defaultCategory = option.value;
      break;
    }
  }

  // Default should allow all sizes <= spot's category
  const sizeHierarchy: SizeCategory[] = SIZE_CATEGORY_OPTIONS.map(
    (opt) => opt.value
  );
  const expectedDefaults = sizeHierarchy.slice(
    0,
    sizeHierarchy.indexOf(defaultCategory) + 1
  );

  // Check if allowedSizes differs from default
  return !arraysEqual(
    allowedSizes.slice().sort(),
    expectedDefaults.slice().sort()
  );
};

// Format allowed sizes as abbreviated string (uses shared formatter for consistency)
const formatAllowedSizes = formatSizeCategories;

// Format dwell time in minutes to readable string
const formatDwellTime = (minutes: number): string => {
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 1440) return `${Math.floor(minutes / 60)}h`;
  return `${Math.floor(minutes / 1440)}d`;
};

// ============================================================================
// Spot Silhouette Overlay Drawing
// ============================================================================

// Colors for reference point positions - brighter, more saturated
const REFERENCE_POINT_COLORS: Record<string, string> = {
  north: "#00E676", // Bright green
  south: "#FF6D00", // Bright orange
  east: "#40C4FF", // Bright blue
  west: "#E040FB", // Bright purple
  center: "#FFEA00", // Bright yellow
};

// Colors for orientation types - high contrast
const ORIENTATION_COLORS: Record<string, string> = {
  nose_to_line: "#FF4081", // Bright pink - arrow pointing IN (nose toward line)
  tail_to_line: "#00E5FF", // Bright cyan - arrow pointing OUT (tail toward line)
  center: "#FFEA00", // Yellow - bidirectional
  overhang: "#FF9100", // Orange - with extension
};

/**
 * Draw a 4-way cross indicator (for center reference point)
 */
const drawCenterCross = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number
): void => {
  const armLength = size;
  const armWidth = size * 0.4;

  ctx.fillStyle = "#E53935"; // Red

  // Horizontal arm
  ctx.fillRect(x - armLength, y - armWidth / 2, armLength * 2, armWidth);
  // Vertical arm
  ctx.fillRect(x - armWidth / 2, y - armLength, armWidth, armLength * 2);
};

/**
 * Draw an overhang indicator - distinct shape showing tail extending beyond spot
 * Shape: Arrow body with horizontal "tail fin" bars extending outward
 */
const drawOverhangIndicator = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  rotation: number
): void => {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rotation);

  const fillColor = "#FF9100"; // Orange for overhang
  ctx.fillStyle = fillColor;
  ctx.strokeStyle = fillColor;

  // Main arrow body pointing toward edge
  const bodyWidth = size * 0.35;
  const bodyHeight = size * 0.6;
  ctx.fillRect(-bodyWidth / 2, -size * 0.1, bodyWidth, bodyHeight);

  // Arrow head (pointing toward reference edge)
  const headWidth = size * 0.7;
  const headHeight = size * 0.4;
  ctx.beginPath();
  ctx.moveTo(0, -size * 0.1 - headHeight);
  ctx.lineTo(-headWidth / 2, -size * 0.1);
  ctx.lineTo(headWidth / 2, -size * 0.1);
  ctx.closePath();
  ctx.fill();

  // Overhang "tail fins" - horizontal bars at the back showing extension
  const finWidth = size * 0.9;
  const finHeight = size * 0.2;
  const finY = bodyHeight - size * 0.1;

  // Draw tail fin bar
  ctx.fillRect(-finWidth / 2, finY, finWidth, finHeight);

  // Draw dashed extension lines from fin ends (showing overhang beyond spot)
  ctx.lineWidth = size * 0.12;
  ctx.setLineDash([size * 0.1, size * 0.08]);
  ctx.lineCap = "round";

  // Left extension
  ctx.beginPath();
  ctx.moveTo(-finWidth / 2, finY + finHeight / 2);
  ctx.lineTo(-finWidth / 2 - size * 0.4, finY + finHeight / 2);
  ctx.stroke();

  // Right extension
  ctx.beginPath();
  ctx.moveTo(finWidth / 2, finY + finHeight / 2);
  ctx.lineTo(finWidth / 2 + size * 0.4, finY + finHeight / 2);
  ctx.stroke();

  ctx.setLineDash([]);
  ctx.restore();
};

/**
 * Draw a directional arrow indicator (for nose/tail reference points)
 * Color indicates orientation type:
 * - Pink (#FF4081): nose_to_line - nose at reference edge
 * - Cyan (#00E5FF): tail_to_line - tail at reference edge
 */
const drawDirectionalArrow = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  rotation: number, // 0 = up, PI = down, PI/2 = right, -PI/2 = left
  orientation: OrientationType = "nose_to_line"
): void => {
  // Use distinct shape for overhang
  if (orientation === "overhang") {
    drawOverhangIndicator(ctx, x, y, size, rotation);
    return;
  }

  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rotation);

  // Color based on orientation type
  const fillColor = orientation === "nose_to_line"
    ? "#FF4081"  // Pink - nose at edge
    : "#00E5FF"; // Cyan - tail at edge

  ctx.fillStyle = fillColor;

  // Arrow body (rectangle)
  const bodyWidth = size * 0.4;
  const bodyHeight = size * 0.8;
  ctx.fillRect(-bodyWidth / 2, -size * 0.2, bodyWidth, bodyHeight);

  // Arrow head (triangle)
  const headWidth = size * 0.8;
  const headHeight = size * 0.5;
  ctx.beginPath();
  ctx.moveTo(0, -size * 0.2 - headHeight);
  ctx.lineTo(-headWidth / 2, -size * 0.2);
  ctx.lineTo(headWidth / 2, -size * 0.2);
  ctx.closePath();
  ctx.fill();

  ctx.restore();
};

/**
 * Draw an arrowhead at a given position with outline
 */
const drawArrowhead = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  rotation: number,
  fillColor: string
): void => {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rotation);

  // Draw outline first
  ctx.beginPath();
  ctx.moveTo(0, -2);
  ctx.lineTo(-size / 2 - 2, size + 2);
  ctx.lineTo(size / 2 + 2, size + 2);
  ctx.closePath();
  ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
  ctx.fill();

  // Draw colored arrowhead
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(-size / 2, size);
  ctx.lineTo(size / 2, size);
  ctx.closePath();
  ctx.fillStyle = fillColor;
  ctx.fill();

  ctx.restore();
};

/**
 * Draw an orientation arrow showing aircraft direction
 */
const drawOrientationArrow = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  orientation: OrientationType,
  angleRadians: number,
  length: number
): void => {
  const color = ORIENTATION_COLORS[orientation] || "#888";

  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angleRadians);

  // Draw shadow/outline for shaft
  ctx.strokeStyle = "rgba(0, 0, 0, 0.5)";
  ctx.lineWidth = 3;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(0, -length / 2);
  ctx.lineTo(0, length / 2);
  ctx.stroke();

  // Draw colored shaft
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(0, -length / 2);
  ctx.lineTo(0, length / 2);
  ctx.stroke();

  // Draw arrowhead based on orientation
  const headSize = length * 0.5;
  if (orientation === "nose_to_line") {
    drawArrowhead(ctx, 0, -length / 2, headSize, Math.PI, color);
  } else if (orientation === "tail_to_line") {
    drawArrowhead(ctx, 0, length / 2, headSize, 0, color);
  } else if (orientation === "center") {
    drawArrowhead(ctx, 0, -length / 2, headSize * 0.8, Math.PI, color);
    drawArrowhead(ctx, 0, length / 2, headSize * 0.8, 0, color);
  } else if (orientation === "overhang") {
    drawArrowhead(ctx, 0, length / 2, headSize, 0, color);
    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = color;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(0, length / 2);
    ctx.lineTo(0, length * 1.3);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  ctx.restore();
};

/**
 * Get the position for a reference point relative to spot bounds
 */
const getReferencePointPosition = (
  pointName: string,
  bounds: { width: number; height: number }
): { x: number; y: number; arrowAngle: number } => {
  switch (pointName) {
    case "north":
      return { x: 0.5, y: 0.08, arrowAngle: 0 };
    case "south":
      return { x: 0.5, y: 0.92, arrowAngle: Math.PI };
    case "east":
      return { x: 0.92, y: 0.5, arrowAngle: Math.PI / 2 };
    case "west":
      return { x: 0.08, y: 0.5, arrowAngle: -Math.PI / 2 };
    case "center":
    default:
      return { x: 0.5, y: 0.5, arrowAngle: 0 };
  }
};

/**
 * Draw aircraft silhouette with reference point indicator inside a spot.
 */
const drawSpotSilhouetteOverlay = (
  ctx: CanvasRenderingContext2D,
  shape: Shape,
  state: CanvasState
): void => {
  if (state.zoomLevel < 0.5) return;

  const referencePoints = shape.reference_points;
  if (!referencePoints || referencePoints.length === 0) return;

  const bounds = getSpotBounds(shape.points);
  const spotWidthFeet = bounds.width / state.pixelsToFeetRatio;

  const aircraft = getCanonicalAircraftForSpot(spotWidthFeet);
  const aircraftBounds = getAircraftBounds(aircraft.coordinates);

  const spotCenter = {
    x: bounds.minX + bounds.width / 2,
    y: bounds.minY + bounds.height / 2,
  };

  const spotAngle = Math.atan2(
    shape.points[1].y - shape.points[0].y,
    shape.points[1].x - shape.points[0].x
  );

  // Use first reference point to determine aircraft orientation
  const primaryRef = referencePoints[0];
  const primaryOrientation = primaryRef.validOrientations[0] || "nose_to_line";

  // Determine aircraft rotation based on reference point and orientation
  // Aircraft nose points up (toward y=0) in original coordinates
  let aircraftRotation = 0;

  // For rotation purposes, treat overhang like tail_to_line (tail at reference edge)
  const isNoseAtEdge = primaryOrientation === "nose_to_line";

  // Reference point position determines which edge the reference line is at
  // Orientation determines if nose or tail faces that edge
  if (primaryRef.name === "north") {
    // Reference line at north (top)
    aircraftRotation = isNoseAtEdge ? 0 : Math.PI;
  } else if (primaryRef.name === "south") {
    // Reference line at south (bottom)
    aircraftRotation = isNoseAtEdge ? Math.PI : 0;
  } else if (primaryRef.name === "east") {
    // Reference line at east (right)
    aircraftRotation = isNoseAtEdge ? Math.PI / 2 : -Math.PI / 2;
  } else if (primaryRef.name === "west") {
    // Reference line at west (left)
    aircraftRotation = isNoseAtEdge ? -Math.PI / 2 : Math.PI / 2;
  }
  // Center reference point: keep aircraft upright (rotation = 0)

  const padding = 0.08;
  const availableWidth = bounds.width * (1 - padding * 2);
  const availableHeight = bounds.height * (1 - padding * 2);

  const scaleX = availableWidth / aircraftBounds.width;
  const scaleY = availableHeight / aircraftBounds.height;
  const scale = Math.min(scaleX, scaleY);

  ctx.save();
  ctx.translate(spotCenter.x, spotCenter.y);
  ctx.rotate(spotAngle);

  // Draw aircraft silhouette
  ctx.save();
  ctx.rotate(aircraftRotation);
  ctx.scale(scale, scale);

  const coords = aircraft.coordinates;
  const offsetX = aircraftBounds.centerX;
  const offsetY = aircraftBounds.centerY;

  ctx.beginPath();
  ctx.moveTo(coords[0][0] - offsetX, coords[0][1] - offsetY);
  for (let i = 1; i < coords.length; i++) {
    ctx.lineTo(coords[i][0] - offsetX, coords[i][1] - offsetY);
  }
  ctx.closePath();

  // Light gray fill
  ctx.fillStyle = "rgba(200, 200, 200, 0.4)";
  ctx.fill();

  // Dark outline
  ctx.strokeStyle = "rgba(50, 50, 50, 0.8)";
  ctx.lineWidth = 2.5 / scale;
  ctx.stroke();

  ctx.restore();

  // Draw indicator based on reference point type
  const indicatorSize = Math.max(8, 12 * state.zoomLevel);

  if (primaryRef.name === "center") {
    drawCenterCross(ctx, 0, 0, indicatorSize);
  } else {
    // Arrow points toward the reference edge (outward from aircraft center)
    let arrowRotation = 0;
    let arrowX = 0;
    let arrowY = 0;

    const edgeOffset = Math.min(bounds.width, bounds.height) * 0.35;

    if (primaryRef.name === "north") {
      arrowY = -edgeOffset;
      arrowRotation = 0; // Point up toward north edge
    } else if (primaryRef.name === "south") {
      arrowY = edgeOffset;
      arrowRotation = Math.PI; // Point down toward south edge
    } else if (primaryRef.name === "east") {
      arrowX = edgeOffset;
      arrowRotation = Math.PI / 2; // Point right toward east edge
    } else if (primaryRef.name === "west") {
      arrowX = -edgeOffset;
      arrowRotation = -Math.PI / 2; // Point left toward west edge
    }

    drawDirectionalArrow(ctx, arrowX, arrowY, indicatorSize, arrowRotation, primaryOrientation);
  }

  ctx.restore();
};

// Format reference points with orientations as abbreviated string
const formatReferencePoints = (referencePoints: ReferencePoint[]): string => {
  const pointAbbrevs: Record<string, string> = {
    north: "N",
    south: "S",
    east: "E",
    west: "W",
    center: "C",
  };

  const orientAbbrevs: Record<string, string> = {
    nose_to_line: "N",
    tail_to_line: "T",
    center: "C",
  };

  // Format each reference point with its orientations
  const formatted = referencePoints.map((rp) => {
    const point = pointAbbrevs[rp.name] || rp.name.charAt(0).toUpperCase();
    const orients = rp.validOrientations
      .map((o) => orientAbbrevs[o] || o.charAt(0).toUpperCase())
      .join("");
    return `${point}:${orients}`;
  });

  return formatted.join(" ");
};

// Draw property badges for a Spot shape
const drawSpotPropertyBadges = (
  context: CanvasRenderingContext2D,
  shape: Shape,
  state: CanvasState
): void => {
  // Early exit if zoom too low
  if (state.zoomLevel < 0.5) return;

  const metadata = shape.metadata as SpotMetadata | undefined;
  if (!metadata) return;

  const bounds = getSpotBounds(shape.points);
  const baseFontSize = 8;
  const fontSize = baseFontSize * Math.max(0.8, state.zoomLevel);
  const padding = 3;
  const badgeSpacing = 2; // Space between badges in the column

  // Calculate spot size in feet
  const spotWidthFeet = bounds.width / state.pixelsToFeetRatio;

  // Collect badge texts and colors (we'll position them in a row later)
  const badgeData: Array<{ text: string; color: string }> = [];

  // Reference Points & Orientations - draw silhouette overlay instead of text badge
  if (shape.reference_points && shape.reference_points.length > 0) {
    drawSpotSilhouetteOverlay(context, shape, state);
  }

  // Allowed Sizes (only if non-default)
  if (
    metadata.allowedSizes &&
    isNonDefaultSizes(spotWidthFeet, metadata.allowedSizes)
  ) {
    badgeData.push({
      text: formatAllowedSizes(metadata.allowedSizes),
      color: "#4444FF",
    });
  }

  // Egress Lanes
  if (metadata.egressLanes && metadata.egressLanes.length > 0) {
    badgeData.push({
      text: `${metadata.egressLanes.length}L`,
      color: "#44AA44",
    });
  }

  // Gap Spot
  if (metadata.isGapSpot) {
    badgeData.push({
      text: "GAP",
      color: "#FFAA44",
    });
  }

  // Min Dwell
  if (metadata.min_dwell_minutes) {
    badgeData.push({
      text: `${formatDwellTime(metadata.min_dwell_minutes)}+`,
      color: "#AA44FF",
    });
  }

  // Max Dwell
  if (metadata.max_dwell_minutes) {
    badgeData.push({
      text: `${formatDwellTime(metadata.max_dwell_minutes)} max`,
      color: "#FF4444",
    });
  }

  // Disabled
  if (metadata.disabled) {
    badgeData.push({
      text: "X",
      color: "#FF4444",
    });
  }

  // If no badges, return early
  if (badgeData.length === 0) {
    return;
  }

  // Set up font for measuring
  context.save();
  context.font = `600 ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto`;

  // Calculate widths for all badges and find max width for right-alignment
  const badgeWidths = badgeData.map((badge) => {
    const metrics = context.measureText(badge.text);
    return metrics.width + padding * 2;
  });
  const pillHeight = fontSize + padding * 2;

  // Get the right edge of the spot (points 1 and 2 in a quad)
  // Spot points are typically: [top-left, top-right, bottom-right, bottom-left]
  const topRight = shape.points[1];
  const bottomRight = shape.points[2];

  // Calculate angle of right edge
  const rightEdgeAngle = Math.atan2(
    bottomRight.y - topRight.y,
    bottomRight.x - topRight.x
  );

  // Calculate perpendicular angle (pointing inward from right edge)
  const perpAngle = rightEdgeAngle - Math.PI / 2;

  // Position: start from bottom-right corner, move inward perpendicular to edge
  const inset = 8;
  let currentX = bottomRight.x + inset * Math.cos(perpAngle);
  let currentY = bottomRight.y + inset * Math.sin(perpAngle);

  // Move up along the right edge to position first badge
  const totalHeight =
    badgeData.length * pillHeight + (badgeData.length - 1) * badgeSpacing;
  currentX -= totalHeight * Math.cos(rightEdgeAngle);
  currentY -= totalHeight * Math.sin(rightEdgeAngle);

  // Render badges in a vertical column (horizontal text, positioned along rotated edge)
  badgeData.forEach((badge, index) => {
    const pillWidth = badgeWidths[index];

    // Calculate center position for this badge (move down along right edge)
    const badgeCenterX = currentX + (pillHeight / 2) * Math.cos(rightEdgeAngle);
    const badgeCenterY = currentY + (pillHeight / 2) * Math.sin(rightEdgeAngle);

    // Draw background pill (horizontal, no rotation)
    context.fillStyle = badge.color;
    context.globalAlpha = 0.7;
    context.beginPath();
    context.roundRect(
      badgeCenterX - pillWidth,
      badgeCenterY - pillHeight / 2,
      pillWidth,
      pillHeight,
      4
    );
    context.fill();

    // Draw text (horizontal, right-aligned)
    context.globalAlpha = 0.9;
    context.fillStyle = "#FFFFFF";
    context.textAlign = "right";
    context.textBaseline = "middle";
    context.fillText(badge.text, badgeCenterX - padding, badgeCenterY);

    // Move to next badge position (move down along right edge)
    const offsetDelta = pillHeight + badgeSpacing;
    currentX += offsetDelta * Math.cos(rightEdgeAngle);
    currentY += offsetDelta * Math.sin(rightEdgeAngle);
  });

  context.restore();
};

export const lineStyleToDash = (lineStyle: string) => {
  switch (lineStyle) {
    case "dashed":
      return [3, 3];
    case "dotted":
      return [2, 2];
    case "solid":
    default:
      return [];
  }
};

/**
 * Draw fixed guide lines 30px from each edge of the canvas
 */
export const drawGuideLines = (
  context: CanvasRenderingContext2D,
  canvasWidth: number,
  canvasHeight: number
) => {
  const inset = 30; // 30px from edges
  const thickness = 0.5; // 0.5px thickness

  context.save();
  context.strokeStyle = "#00ff19ff"; // Bright green color for better visibility
  context.lineWidth = thickness;
  context.setLineDash([]);

  context.beginPath();

  // Top line
  context.moveTo(inset, inset);
  context.lineTo(canvasWidth - inset, inset);

  // Right line
  context.moveTo(canvasWidth - inset, inset);
  context.lineTo(canvasWidth - inset, canvasHeight - inset);

  // Bottom line
  context.moveTo(canvasWidth - inset, canvasHeight - inset);
  context.lineTo(inset, canvasHeight - inset);

  // Left line
  context.moveTo(inset, canvasHeight - inset);
  context.lineTo(inset, inset);

  context.stroke();
  context.restore();
};

export const createImagePattern = async (
  canvas: HTMLCanvasElement,
  imagePath: string,
  alpha: number = 1.0 // Alpha value between 0 (fully transparent) and 1 (fully opaque)
): Promise<CanvasPattern | null> => {
  return new Promise((resolve, reject) => {
    const context = canvas.getContext("2d");
    const image = new Image();
    image.src = imagePath;

    image.onload = () => {
      // Create an offscreen canvas
      const offscreenCanvas = document.createElement("canvas");
      offscreenCanvas.width = image.width;
      offscreenCanvas.height = image.height;
      const offscreenContext = offscreenCanvas.getContext("2d");

      if (offscreenContext) {
        // Draw the image onto the offscreen canvas
        offscreenContext.globalAlpha = alpha; // Set alpha transparency
        offscreenContext.drawImage(image, 0, 0);

        // Create a pattern from the offscreen canvas
        const pattern = context.createPattern(offscreenCanvas, "repeat");
        if (pattern) {
          resolve(pattern);
        } else {
          reject(new Error("Failed to create pattern"));
        }
      } else {
        reject(new Error("Failed to get context for offscreen canvas"));
      }
    };

    image.onerror = () => {
      reject(new Error(`Failed to load image at ${imagePath}`));
    };
  });
};

/**
 * Draw a calibration point as a target icon (matching GpsFixed icon)
 */
export const drawCalibrationPoint = (
  context: CanvasRenderingContext2D,
  point: CalibrationPoint,
  pixelsToFeetRatio: number,
  isHighlighted: boolean = false
) => {
  // Convert relative coordinates to pixel coordinates
  // point.x and point.y are relative (0.0 to 1.0)
  const canvasWidth = context.canvas.width;
  const canvasHeight = context.canvas.height;

  const x = point.x * canvasWidth;
  const y = point.y * canvasHeight;

  const size = 20; // Size of the target icon
  const radius = size / 2;

  context.save();

  // Set colors based on highlight state
  const strokeColor = isHighlighted ? "#00ff00" : "#e74c3c"; // Bright green for hover, red for normal
  const strokeWidth = isHighlighted ? 3 : 2;

  context.strokeStyle = strokeColor;
  context.lineWidth = strokeWidth;
  context.setLineDash([]);

  // Draw outer circle
  context.beginPath();
  context.arc(x, y, radius, 0, 2 * Math.PI);
  context.stroke();

  // Draw inner circle
  context.beginPath();
  context.arc(x, y, radius * 0.6, 0, 2 * Math.PI);
  context.stroke();

  // Draw center dot
  context.fillStyle = strokeColor;
  context.beginPath();
  context.arc(x, y, radius * 0.2, 0, 2 * Math.PI);
  context.fill();

  // Draw crosshairs
  const crosshairLength = radius * 1.4;
  context.lineWidth = strokeWidth;

  // Horizontal line
  context.beginPath();
  context.moveTo(x - crosshairLength, y);
  context.lineTo(x + crosshairLength, y);
  context.stroke();

  // Vertical line
  context.beginPath();
  context.moveTo(x, y - crosshairLength);
  context.lineTo(x, y + crosshairLength);
  context.stroke();

  // No text label needed for calibration points

  context.restore();
};

/**
 * Draw a reference aircraft on the canvas for scale visualization.
 * The aircraft is rendered as an image, centered at the given position,
 * and can be rotated.
 */
export const drawReferenceAircraft = (
  context: CanvasRenderingContext2D,
  aircraft: Aircraft,
  position: { x: number; y: number; angle: number }, // in pixels
  loadedImage: HTMLImageElement | null,
  isSelected: boolean,
  pixelsToFeetRatio: number,
  showMeasurements: boolean = false,
  isMetric: boolean = false
) => {
  if (!aircraft || !loadedImage) {
    return;
  }

  // Calculate pixel dimensions from aircraft dimensions in feet
  const widthPixels = aircraft.wingspan * pixelsToFeetRatio;
  const heightPixels = aircraft.length * pixelsToFeetRatio;

  context.save();

  // Translate to position (center of aircraft)
  context.translate(position.x, position.y);

  // Rotate around center
  context.rotate(position.angle);

  // Draw a semi-transparent background for better visibility
  context.globalAlpha = 0.85;

  // Draw the aircraft image centered
  context.drawImage(
    loadedImage,
    -widthPixels / 2,
    -heightPixels / 2,
    widthPixels,
    heightPixels
  );

  context.globalAlpha = 1.0;

  // Draw selection indicator if selected
  if (isSelected) {
    context.strokeStyle = "#2196F3";
    context.lineWidth = 3;
    context.setLineDash([8, 4]);
    context.strokeRect(
      -widthPixels / 2 - 4,
      -heightPixels / 2 - 4,
      widthPixels + 8,
      heightPixels + 8
    );
    context.setLineDash([]);
  }

  // Draw dimension labels if measurements are shown
  if (showMeasurements) {
    context.rotate(-position.angle); // Reset rotation for text

    const dimensionText = isMetric
      ? `${(aircraft.wingspan * 0.3048).toFixed(1)}m x ${(aircraft.length * 0.3048).toFixed(1)}m`
      : `${aircraft.wingspan.toFixed(0)}ft x ${aircraft.length.toFixed(0)}ft`;

    context.font = "12px Lexend Deca";
    context.fillStyle = "#333";
    context.textAlign = "center";
    context.textBaseline = "top";

    // Dimension label (below aircraft)
    context.fillText(dimensionText, 0, heightPixels / 2 + 8);
  }

  context.restore();
};
